import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';
import { UtilityService } from 'app/services/utility.service';
import { environment } from 'environments/environment';
import { ApInvoiceDuplicateSearchRequest } from 'app/models/form/ApInvoiceDuplicateSearchRequest';
import { PaymentAdjustmentFormView } from 'app/models/form/PaymentAdjustmentFormView';
import { PaymentAdjustmentFormCreator } from 'app/models/form/PaymentAdjustmentFormCreator';
import { PaymentAdjustmentSummaryView } from 'app/models/form/PaymentAdjustmentSummaryView';

@Injectable({
	providedIn: 'root',
})
export class PaymentAdjustmentFormService {
	private readonly urlPath = 'payment-adjustment-form';

	constructor(private readonly httpClient: HttpClient, private readonly utilityService: UtilityService) {}

	createForm(clientId: string, form: PaymentAdjustmentFormCreator): Observable<PaymentAdjustmentFormView> {
		return this.httpClient.post<PaymentAdjustmentFormView>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}`,
			this.setupRequest(form)
		);
	}

	updateForm(
		clientId: string,
		formId: string,
		request: PaymentAdjustmentFormView
	): Observable<PaymentAdjustmentFormView> {
		return this.httpClient.put<PaymentAdjustmentFormView>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}`,
			this.setupRequest(request)
		);
	}

	getForm(clientId: string, id: string): Observable<PaymentAdjustmentFormView> {
		return this.httpClient.get<PaymentAdjustmentFormView>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}`
		);
	}

	getFormsFor(
		clientId: string,
		userId: string,
		search: FormSearch
	): Observable<BaseDashboard<PaymentAdjustmentFormView>> {
		const url = `${environment.apiUrl}forms/clients/${clientId}/${
			this.urlPath
		}/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<PaymentAdjustmentFormView>>(url);
	}

	rejectForm(
		clientId: string,
		formId: string,
		rejectionReason: { reason: string; workflowId: number }
	): Observable<PaymentAdjustmentFormView> {
		return this.httpClient.post<PaymentAdjustmentFormView>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/decline`,
			rejectionReason
		);
	}

	approveForm(clientId: string, formId: string, userId: string): Observable<PaymentAdjustmentFormView> {
		return this.httpClient.post<PaymentAdjustmentFormView>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/approve`,
			{ userId }
		);
	}

	sendBack(clientId: string, formId: string): Observable<PaymentAdjustmentFormView> {
		return this.httpClient.post<PaymentAdjustmentFormView>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/review`,
			{}
		);
	}

	submitForApproval(clientId: string, id: string): Observable<PaymentAdjustmentFormView> {
		return this.httpClient.post<PaymentAdjustmentFormView>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/approval`,
			{}
		);
	}

	searchSourcePayment(
		clientId: string,
		request: ApInvoiceDuplicateSearchRequest
	): Observable<PaymentAdjustmentFormView[]> {
		return this.httpClient.post<PaymentAdjustmentFormView[]>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/search-source-payment`,
			request
		);
	}

	getSourcePaymentSnapshot(clientId: string, source: string, sourceId: string): Observable<PaymentAdjustmentFormView> {
		const encodedSource = encodeURIComponent(source);
		return this.httpClient.get<PaymentAdjustmentFormView>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/source-snapshot/${encodedSource}/${sourceId}`
		);
	}

	getLinkedPaymentAdjustmentFormURL(clientId: string, sourceId: string): Observable<{ url: string }> {
		return this.httpClient.get<{ url: string }>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/linked-payment-adjustment-form-url/${sourceId}`
		);
	}

	getAdjustmentSummary(clientId: string, sourceId: string): Observable<PaymentAdjustmentSummaryView | null> {
		return this.httpClient.get<PaymentAdjustmentSummaryView | null>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/adjustment-summary/${sourceId}`
		);
	}

	getBySource(clientId: string, sourceId: string): Observable<PaymentAdjustmentSummaryView[]> {
		return this.httpClient.get<PaymentAdjustmentSummaryView[]>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/by-source/${sourceId}`
		);
	}

	uploadFile(clientId: string, id: string, file: File): Observable<HttpEvent<unknown>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest(
			'POST',
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/docs`,
			formData,
			{
				reportProgress: true,
				responseType: 'json',
			}
		);
		return this.httpClient.request(req);
	}

	deleteDocument(clientId: string, formId: string, docId: string): Observable<unknown> {
		return this.httpClient.delete<unknown>(
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/docs/${docId}`
		);
	}

	private setupRequest(request: PaymentAdjustmentFormCreator | PaymentAdjustmentFormView): FormData {
		const formData: FormData = new FormData();
		formData.append('model', JSON.stringify(request));
		return formData;
	}
}
